import { useIsMobile } from '@/hooks/use-mobile';
import type { ToolbarFilterOption } from './FilterPanel';
import { GenericToolbarDesktop } from './GenericToolbarDesktop';
import { GenericToolbarMobile } from './GenericToolbarMobile';
import type { ReactNode } from 'react';

/** Shared view-mode union across every index-page toolbar. */
export type ToolbarViewMode = 'grid' | 'table' | 'gantt' | 'jobs';

// Re-exported so pages building `columnFilterOptions` have one import to reach for.
export type { ToolbarFilterOption };

/** Config for a non-standard third view-mode button (e.g. Work Orders' "Jobs"). */
export interface ExtraViewMode {
    value: ToolbarViewMode;
    icon: ReactNode;
    label: string;
}

export interface FilterDefinition {
    id: string;
    label: string;
}

interface BuildFilterDefinitionsArgs {
    quickFilterDefinitions: FilterDefinition[];
    columnDefinitions: FilterDefinition[];
    extraFilterDefinitions?: FilterDefinition[];
    excludeFromMoreFilters?: string[];
    showMoreFilters?: boolean;
}

/**
 * The one list of filters a toolbar offers: quick filters first, then the remaining
 * table columns, then column-less extras (a year picker, say).
 *
 * Deduped by id — a definition that appears in two of the three lists must render
 * once, or the filter shows up twice with a duplicate React key. Desktop and mobile
 * share this so they cannot drift apart again.
 */
export function buildFilterDefinitions({
    quickFilterDefinitions,
    columnDefinitions,
    extraFilterDefinitions = [],
    excludeFromMoreFilters = [],
    showMoreFilters = true,
}: BuildFilterDefinitionsArgs): FilterDefinition[] {
    const seen = new Set<string>();

    return [
        ...quickFilterDefinitions,
        ...(showMoreFilters ? columnDefinitions.filter((column) => !excludeFromMoreFilters.includes(column.id)) : []),
        ...extraFilterDefinitions,
    ].filter((definition) => {
        if (seen.has(definition.id)) {
            return false;
        }
        seen.add(definition.id);
        return true;
    });
}

interface GenericToolbarProps {
    quickFilterButtonLabel: string;
    quickFilterDefinitions: Array<{ id: string; label: string }>;
    columnDefinitions: Array<{ id: string; label: string }>;
    columnFilterOptions: Record<string, ToolbarFilterOption[]>;
    /** Optional per-filter loading flags. A filter that is `true` here renders a spinner card until its options arrive. */
    columnFilterLoading?: Record<string, boolean>;
    columnFilters: Record<string, string[]>;
    columnFilterSearch: Record<string, string>;
    setColumnFilterSearch: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    toggleSingleFilter: (columnId: string, value: string) => void;
    handleFilterChange: (column: string, values: string[]) => void;
    clearFilters: () => void;
    isFilterOpen: boolean;
    setIsFilterOpen: (open: boolean) => void;
    isSortOpen: boolean;
    setIsSortOpen: (open: boolean) => void;
    sortColumn: string | null;
    sortDirection: 'asc' | 'desc' | null;
    handleSortChange: (column: string, direction: 'asc' | 'desc') => void;
    clearSort: () => void;
    search: string;
    setSearch: (value: string) => void;
    /** Only read when the per-page select is shown — omit it alongside `showPerPageSelect={false}`. */
    perPage?: number;
    onPerPageChange?: (perPage: number) => void;
    viewMode: ToolbarViewMode;
    setViewMode: (mode: ToolbarViewMode) => void;
    showGanttViewToggle?: boolean;
    /** When false, hides the entire table/grid/(extra) view-mode toggle group. Defaults to true. */
    showViewToggle?: boolean;
    /** Renders an additional view-mode button (value + icon + label) after table/grid. */
    extraViewMode?: ExtraViewMode;
    showPerPageSelect?: boolean;
    visibleColumns: Set<string>;
    handleColumnShow: (columnId: string) => void;
    handleColumnHide: (columnId: string) => void;
    filteredCount: number;
    resourceName: string;
    resourceNamePlural: string;
    sortableColumns?: Array<{ id: string; label: string }>;
    excludeFromMoreFilters?: string[];
    /**
     * Filter groups that have no matching table column — a year picker, say.
     * Kept apart from columnDefinitions because that list also drives the
     * show/hide-columns menu, where these would appear as phantom columns.
     */
    extraFilterDefinitions?: Array<{ id: string; label: string }>;
    customFilters?: ReactNode;
    /** Where customFilters sits relative to the checkbox groups. */
    customFiltersPosition?: 'start' | 'end';
    inlineFilters?: ReactNode;
    showMoreFilters?: boolean;
}

export function GenericToolbar(props: GenericToolbarProps) {
    const isMobile = useIsMobile();
    const { showGanttViewToggle = false, showViewToggle = true, showPerPageSelect = true, ...rest } = props;

    if (isMobile) {
        return (
            <GenericToolbarMobile
                {...rest}
                showGanttViewToggle={showGanttViewToggle}
                showViewToggle={showViewToggle}
                showPerPageSelect={showPerPageSelect}
            />
        );
    }

    return (
        <GenericToolbarDesktop
            {...rest}
            showGanttViewToggle={showGanttViewToggle}
            showViewToggle={showViewToggle}
            showPerPageSelect={showPerPageSelect}
        />
    );
}
